import React, { useState } from 'react';
import {
    Dialog, DialogFooter, DialogType, PrimaryButton, DefaultButton, TextField,
    MessageBar, MessageBarType, Toggle, Stack, ChoiceGroup,
} from '@fluentui/react';
import NumberField from '../Inputs/NumberField';
import PromoPricingFields from './PromoPricingFields';
import { PROMO_MARKUP_PERCENT, isDiscountPromo } from '../../models/ProductPromo';
import { useBulkApplyProductPromoMutation } from '../../store/endpoints/StorefrontSettingsEndpoints';

interface CreateProductPromoDialogProps {
    catalog: string;
    storefront: string;
    productIds: string[];
    onDismiss: () => void;
    onApplied: () => void;
}

// Promos are advertised from a cache and charged fresh, so a promo that ENDS during trading hours can
// still be shown for a short window after it has stopped applying. Ending outside trading hours keeps
// that window empty. See the promo design notes on cache staleness.
const QUIET_HOUR = 2;
const BUSINESS_START = 7;
const BUSINESS_END = 19;

/** Local datetime string for an <input type="datetime-local">, which has no timezone of its own. */
const toLocalInput = (d: Date) => {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultEnd = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(QUIET_HOUR, 0, 0, 0);
    return toLocalInput(d);
};

const CreateProductPromoDialog: React.FC<CreateProductPromoDialogProps> = (props) => {
    const [name, setName] = useState<string>('');
    const [markupType, setMarkupType] = useState<string>(PROMO_MARKUP_PERCENT);
    const [markup, setMarkup] = useState<number | undefined>();
    const [startsAt, setStartsAt] = useState<string>('');
    const [endsAt, setEndsAt] = useState<string>(defaultEnd());
    const [limitQuantity, setLimitQuantity] = useState<boolean>(false);
    const [allocation, setAllocation] = useState<number | undefined>();
    const [allStorefronts, setAllStorefronts] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    const [applyPromo, applyState] = useBulkApplyProductPromoMutation();

    // The datetime-local value is local wall-clock; the API stores UTC instants, so an explicit
    // conversion here keeps "ends 5pm" meaning 5pm where the MSP is rather than 5pm UTC.
    const toUtc = (local: string) => (local ? new Date(local).toISOString() : undefined);

    const endsInBusinessHours = (() => {
        if (!endsAt) return false;
        const hour = new Date(endsAt).getHours();
        return hour >= BUSINESS_START && hour < BUSINESS_END;
    })();

    const apply = async () => {
        setError(undefined);
        if (markup === undefined) {
            setError(isDiscountPromo(markupType) ? 'Enter the discount percentage.' : 'Enter the promotional markup.');
            return;
        }
        // Mirrors the API's bound. A discount is a percentage off what the shopper pays, so zero is not a
        // promotion and 100 gives the product away.
        if (isDiscountPromo(markupType) && (markup <= 0 || markup >= 100)) {
            setError('The discount must be greater than 0 and less than 100 percent.'); return;
        }
        if (endsAt && startsAt && new Date(endsAt) <= new Date(startsAt)) {
            setError('The promo end must be after its start.'); return;
        }
        if (limitQuantity && (allocation === undefined || allocation <= 0)) {
            setError('Enter how many units the promo covers, or turn the limit off.'); return;
        }
        const result = await applyPromo({
            catalog: props.catalog,
            request: {
                productIds: props.productIds,
                name: name || undefined,
                storefronts: allStorefronts ? [] : [props.storefront],
                markup,
                markupType,
                startsAt: toUtc(startsAt),
                endsAt: toUtc(endsAt),
                allocationQuantity: limitQuantity ? allocation : undefined,
                replaceExisting: true,
            },
        }).unwrap().catch(() => undefined);

        if (!result?.success) { setError(result?.message ?? 'Could not apply the promo.'); return; }
        props.onApplied();
    };

    return (
        <Dialog
            hidden={false}
            onDismiss={props.onDismiss}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Create Promo',
                subText: `A promotional price for the ${props.productIds.length} selected product${props.productIds.length === 1 ? '' : 's'}.`,
            }}
            minWidth={480}
        >
            <Stack tokens={{ childrenGap: 12 }}>
                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

                <TextField
                    label="Promo name"
                    placeholder="Spring Clearance"
                    description="Shown to the shopper as a badge on the product."
                    value={name}
                    onChange={(_, v) => setName(v ?? '')}
                />

                <PromoPricingFields
                    markupType={markupType}
                    markup={markup}
                    onChange={(type, value) => { setMarkupType(type ?? PROMO_MARKUP_PERCENT); setMarkup(value); }}
                />

                <TextField
                    label="Starts"
                    type="datetime-local"
                    description="Leave blank to start immediately."
                    value={startsAt}
                    onChange={(_, v) => setStartsAt(v ?? '')}
                />
                <TextField
                    label="Ends"
                    type="datetime-local"
                    description="Leave blank for no end date."
                    value={endsAt}
                    onChange={(_, v) => setEndsAt(v ?? '')}
                />
                {endsInBusinessHours && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        Prices are cached for up to 30 minutes, so a promo ending during trading hours can
                        still be shown briefly after it stops applying. Checkout always charges the correct
                        price. Ending outside trading hours avoids the mismatch.
                    </MessageBar>
                )}

                <Toggle
                    label="Limit how many units sell at this price"
                    inlineLabel
                    onText="On"
                    offText="Off"
                    checked={limitQuantity}
                    onChange={(_, checked) => setLimitQuantity(checked ?? false)}
                />
                {limitQuantity && (
                    <NumberField
                        label="Units at the promo price"
                        toolTip="Applied per product. Once these are sold the promo stops applying and the normal price returns."
                        value={allocation}
                        onChange={setAllocation}
                    />
                )}

                {/* A choice between two named scopes rather than an on/off. As a toggle its label read
                    as changing meaning when flipped, since the on/off text WAS the choice; radios state
                    both options at once and neither moves. */}
                <ChoiceGroup
                    label="Where this promo applies"
                    selectedKey={allStorefronts ? 'all' : 'this'}
                    options={[
                        { key: 'this', text: 'This storefront only' },
                        { key: 'all', text: 'Every storefront' },
                    ]}
                    onChange={(_, option) => setAllStorefronts(option?.key === 'all')}
                />
            </Stack>

            <DialogFooter>
                <PrimaryButton text="Create Promo" onClick={apply} disabled={applyState.isLoading} />
                <DefaultButton text="Cancel" onClick={props.onDismiss} disabled={applyState.isLoading} />
            </DialogFooter>
        </Dialog>
    );
};

export default CreateProductPromoDialog;
